import React from 'react';
import DOMPurify from 'dompurify';
import {
  Box,
  Stack,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  Pagination,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatDateTime, formatRelativeDistance } from '../../utils/formatters';
import NotificationErrorBoundary from '../NotificationErrorBoundary';

/**
 * Status badge - aceita número ou string da API (v2 — 6 status,
 * docs/PLANO-SUPPORT-TICKETS-V2.md §1.1). A Api serializa enums como string
 * camelCase (JsonStringEnumConverter global, Program.cs) — os números ficam
 * só de defesa/histórico.
 */
const STATUS_MAP = {
  0: { label: 'Aberto', color: 'info' },
  1: { label: 'Em Análise', color: 'warning' },
  2: { label: 'Resolvido', color: 'success' },
  3: { label: 'Fechado', color: 'default' },
  4: { label: 'Aguardando Cliente', color: 'warning' },
  5: { label: 'Aguardando Suporte', color: 'error' },
  'Open': { label: 'Aberto', color: 'info' },
  'open': { label: 'Aberto', color: 'info' },
  'InProgress': { label: 'Em Análise', color: 'warning' },
  'inProgress': { label: 'Em Análise', color: 'warning' },
  'Resolved': { label: 'Resolvido', color: 'success' },
  'resolved': { label: 'Resolvido', color: 'success' },
  'Closed': { label: 'Fechado', color: 'default' },
  'closed': { label: 'Fechado', color: 'default' },
  'WaitingCustomer': { label: 'Aguardando Cliente', color: 'warning' },
  'waitingCustomer': { label: 'Aguardando Cliente', color: 'warning' },
  'WaitingSupport': { label: 'Aguardando Suporte', color: 'error' },
  'waitingSupport': { label: 'Aguardando Suporte', color: 'error' },
};

const PRIORITY_MAP = {
  0: { label: 'Baixa', color: 'info', icon: '↓', show: true },
  1: { label: 'Normal', color: 'default', icon: null, show: false },
  2: { label: 'Alta', color: 'error', icon: '↑', show: true },
  3: { label: 'Urgente', color: 'error', icon: '↑', show: true },
  'Low': { label: 'Baixa', color: 'info', icon: '↓', show: true },
  'low': { label: 'Baixa', color: 'info', icon: '↓', show: true },
  'Normal': { label: 'Normal', color: 'default', icon: null, show: false },
  'normal': { label: 'Normal', color: 'default', icon: null, show: false },
  'High': { label: 'Alta', color: 'error', icon: '↑', show: true },
  'high': { label: 'Alta', color: 'error', icon: '↑', show: true },
  'Urgent': { label: 'Urgente', color: 'error', icon: '↑', show: true },
  'urgent': { label: 'Urgente', color: 'error', icon: '↑', show: true },
};

const TICKET_TYPE_MAP = {
  'suggestion': { label: '💡 Sugestão', color: 'info' },
  'complaint': { label: '😠 Reclamação', color: 'error' },
  'question': { label: '❓ Pergunta', color: 'warning' },
  'bug': { label: '🐛 Relatório de Bug', color: 'error' },
  'feedback': { label: '💬 Feedback', color: 'success' },
  'support': { label: '🆘 Suporte', color: 'primary' },
  'other': { label: 'Outro', color: 'default' },
};

/**
 * TicketList Component
 * Lista de tickets com accordion que expande para baixo mostrando detalhes
 */
export function TicketList({
  tickets = [],
  loading = false,
  error = null,
  totalCount = 0,
  page = 1,
  pageSize = 20,
  onPageChange = () => {},
  selectedTicketId = null,
  onToggleTicket = () => {},
  selectedTicket = null,
  ticketDetailsLoading = false,
  replies = [],
  renderReplyEditor = null,
}) {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!tickets || tickets.length === 0) {
    return (
      <Alert severity="info">
        Você ainda não possui tickets de suporte. Clique em "Novo Ticket" para criar um.
      </Alert>
    );
  }

  const totalPages = Math.ceil(totalCount / pageSize);

  const getStatusInfo = (status) => STATUS_MAP[status] || { label: 'Desconhecido', color: 'default' };
  const getPriorityInfo = (priority) => PRIORITY_MAP[priority] || { label: 'Normal', color: 'primary', icon: '🟢' };
  const getTicketTypeInfo = (type) => TICKET_TYPE_MAP[type?.toLowerCase()] || { label: type || 'Geral', color: 'default' };

  const handleAccordionChange = (ticketId) => (event, isExpanded) => {
    // Se está expandindo, seleciona o ticket; se está recolhendo, deseleciona
    onToggleTicket(isExpanded ? ticketId : null);
  };

  return (
    <Stack spacing={2}>
      {tickets.map((ticket) => {
        const isExpanded = selectedTicketId === ticket.id;
        const statusInfo = getStatusInfo(ticket.status);
        const priorityInfo = getPriorityInfo(ticket.priority);
        const typeInfo = getTicketTypeInfo(ticket.ticketType);

        return (
          <Accordion
            key={ticket.id}
            expanded={isExpanded}
            onChange={handleAccordionChange(ticket.id)}
            sx={{
              border: '1px solid',
              borderColor: isExpanded ? 'primary.main' : 'divider',
              borderRadius: '8px !important',
              '&:before': { display: 'none' },
              boxShadow: isExpanded ? 2 : 1,
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ 
                minHeight: 72,
                '& .MuiAccordionSummary-content': { my: 1.5 }
              }}
            >
              <Box sx={{ width: '100%' }}>
                {/* Linha 1: Subject e Tags */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1, minWidth: 200 }}>
                    {ticket.subject || 'Sem assunto'}
                  </Typography>
                  
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {/* Status */}
                    <Chip
                      label={statusInfo.label}
                      size="small"
                      color={statusInfo.color}
                      sx={{ fontWeight: 500 }}
                    />
                    
                    {/* Prioridade - ocultar se Normal, mostrar com seta se Baixa/Alta */}
                    {priorityInfo.show && (
                      <Chip
                        label={`${priorityInfo.icon} ${priorityInfo.label}`}
                        size="small"
                        color={priorityInfo.color}
                        sx={{ fontWeight: 500 }}
                      />
                    )}
                    
                    {/* Tipo do ticket - com ícone no label */}
                    {ticket.ticketType && (
                      <Chip
                        label={typeInfo.label}
                        size="small"
                        color={typeInfo.color}
                        sx={{ fontWeight: 500 }}
                      />
                    )}
                  </Stack>
                </Stack>

                {/* Linha 2: Metadata */}
                <Stack direction="row" spacing={2} mt={1} flexWrap="wrap" alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    ID: {ticket.id?.substring(0, 8)}...
                  </Typography>
                  
                  <Typography variant="caption" color="text.secondary">
                    Criado{' '}
                    {ticket.createdAt
                      ? formatRelativeDistance(ticket.createdAt)
                      : 'em data desconhecida'}
                  </Typography>

                  {ticket.repliesCount > 0 && (
                    <Chip
                      label={`${ticket.repliesCount} resposta${ticket.repliesCount !== 1 ? 's' : ''}`}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}

                  {ticket.unreadAdminReplies > 0 && (
                    <Chip
                      label={`${ticket.unreadAdminReplies} nova${ticket.unreadAdminReplies !== 1 ? 's' : ''}`}
                      size="small"
                      color="warning"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Stack>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ pt: 0 }}>
              <Divider sx={{ mb: 2 }} />
              
              {ticketDetailsLoading && isExpanded ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={24} />
                </Box>
              ) : isExpanded && selectedTicket ? (
                <Stack spacing={2}>
                  {/* Mensagem original */}
                  {selectedTicket.message && (
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                        Mensagem:
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedTicket.message}
                      </Typography>
                    </Box>
                  )}

                  {/* Contato */}
                  {selectedTicket.contactInfo && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Contato:</strong> {selectedTicket.contactInfo}
                    </Typography>
                  )}

                  {/* Data detalhada */}
                  <Typography variant="caption" color="text.secondary">
                    Criado em:{' '}
                    {selectedTicket.createdAt
                      ? formatDateTime(selectedTicket.createdAt)
                      : 'Data desconhecida'}
                  </Typography>

                  {/* Respostas */}
                  {replies && replies.length > 0 && (
                    <>
                      <Divider />
                      <Typography variant="subtitle2" fontWeight={600}>
                        Respostas ({replies.length})
                      </Typography>
                      <Stack spacing={1.5}>
                        {replies.map((reply) => (
                          <Box
                            key={reply.id}
                            sx={{
                              p: 2,
                              borderRadius: 1,
                              bgcolor: reply.isAdminReply ? 'primary.50' : 'grey.100',
                              borderLeft: reply.isAdminReply ? '3px solid' : 'none',
                              borderColor: 'primary.main',
                            }}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                              <Typography variant="caption" fontWeight={600} color={reply.isAdminReply ? 'primary.main' : 'text.secondary'}>
                                {reply.isAdminReply ? '👤 Suporte' : '👤 Você'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {reply.createdAt
                                  ? formatRelativeDistance(reply.createdAt)
                                  : ''}
                              </Typography>
                            </Stack>
                            <NotificationErrorBoundary>
                              <Typography 
                                variant="body2" 
                                component="div"
                                sx={{
                                  whiteSpace: 'pre-wrap',
                                  '& p': { margin: 0 },
                                  '& p + p': { marginTop: 1 },
                                  '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
                                }}
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(reply.content) }}
                              />
                            </NotificationErrorBoundary>
                          </Box>
                        ))}
                      </Stack>
                    </>
                  )}

                  {replies && replies.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Nenhuma resposta ainda. Use o editor abaixo para enviar uma mensagem.
                    </Typography>
                  )}

                  {/* Reply Editor */}
                  {renderReplyEditor && (
                    <>
                      <Divider />
                      <Typography variant="subtitle2" fontWeight={600}>
                        Enviar Mensagem
                      </Typography>
                      {renderReplyEditor()}
                    </>
                  )}
                </Stack>
              ) : null}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(event, value) => onPageChange(value)}
            color="primary"
          />
        </Box>
      )}
    </Stack>
  );
}

export default TicketList;
